// bm-design-system: autocomplete primitive
import * as React from "react"
import * as PopoverPrimitive from "@radix-ui/react-popover"
import { Loader2, Search, X } from "lucide-react"
import { cn } from "@/lib/utils"

export type AutocompleteSuggestion = {
  /** Stable key, and what `onSelect` is called with. */
  id: string | number
  label: string
  /** Second line — an email, an organization, whatever tells two people apart. */
  hint?: string | null
}

export type AutocompleteProps = {
  /** The text in the box. Controlled: the caller owns it. */
  value: string
  onValueChange: (value: string) => void
  /** Whatever the server last returned for `value`. */
  suggestions: AutocompleteSuggestion[]
  onSelect: (suggestion: AutocompleteSuggestion) => void
  loading?: boolean
  id?: string
  placeholder?: string
  emptyMessage?: string
  /** Shown before anything has been typed. */
  hintMessage?: string
  disabled?: boolean
  className?: string
  "aria-label"?: string
}

/**
 * A search box whose suggestions come from the server.
 *
 * Distinct from `<Combobox>`, which filters a fixed list of options it already
 * holds in the browser. Reach for this one when the list is too long to ship —
 * several hundred user accounts, say — so the matching has to happen in the
 * database and the caller fetches as the user types.
 *
 * The caller owns the query, the debounce and the fetch; this owns the popover,
 * the keyboard model and the empty states. Typing does not select anything, so
 * a page is free to use the same text as a filter while the dropdown offers
 * jump-to targets.
 *
 * Keyboard: ArrowDown / ArrowUp move, Enter takes the highlighted suggestion,
 * Escape closes without choosing.
 */
export function Autocomplete({
  value,
  onValueChange,
  suggestions,
  onSelect,
  loading = false,
  id,
  placeholder = "Search…",
  emptyMessage = "No matches.",
  hintMessage = "Start typing to search.",
  disabled = false,
  className,
  "aria-label": ariaLabel,
}: AutocompleteProps) {
  const [open, setOpen] = React.useState(false)
  const [activeIndex, setActiveIndex] = React.useState(0)
  const inputRef = React.useRef<HTMLInputElement>(null)
  const listRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    setActiveIndex(0)
  }, [suggestions])

  // Keep the highlighted row in view while arrowing through the list.
  React.useEffect(() => {
    if (!open) return
    const active = listRef.current?.querySelector<HTMLElement>('[data-active="true"]')
    active?.scrollIntoView({ block: "nearest" })
  }, [activeIndex, open, suggestions])

  const commit = (suggestion: AutocompleteSuggestion) => {
    setOpen(false)
    onSelect(suggestion)
  }

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault()
      setOpen(true)
      setActiveIndex((index) => Math.min(index + 1, suggestions.length - 1))
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      setActiveIndex((index) => Math.max(index - 1, 0))
    } else if (event.key === "Enter") {
      const suggestion = suggestions[activeIndex]
      // Only intercept Enter when there is something to take. Otherwise it
      // stays the caller's — submitting a form, applying a filter.
      if (open && suggestion) {
        event.preventDefault()
        commit(suggestion)
      }
    } else if (event.key === "Escape") {
      setOpen(false)
    }
  }

  const clear = () => {
    onValueChange("")
    setOpen(false)
    inputRef.current?.focus()
  }

  return (
    <PopoverPrimitive.Root open={open && !disabled} onOpenChange={setOpen}>
      <PopoverPrimitive.Anchor asChild>
        <div className={cn("relative", className)}>
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" />

          <input
            ref={inputRef}
            id={id}
            type="text"
            role="combobox"
            aria-expanded={open}
            aria-autocomplete="list"
            aria-label={ariaLabel}
            autoComplete="off"
            disabled={disabled}
            value={value}
            placeholder={placeholder}
            onChange={(event) => {
              onValueChange(event.target.value)
              setOpen(true)
            }}
            onFocus={() => value.length > 0 && setOpen(true)}
            onKeyDown={onKeyDown}
            className={cn("form-control pl-9", (loading || value) && "pr-9")}
          />

          {loading ? (
            <Loader2 className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-ink-muted" />
          ) : (
            value && (
              <button
                type="button"
                onClick={clear}
                aria-label="Clear search"
                className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 cursor-pointer items-center justify-center rounded-md text-ink-muted hover:bg-surface"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )
          )}
        </div>
      </PopoverPrimitive.Anchor>

      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={6}
          // Focus must stay in the input: the popover is a preview of what is
          // being typed, not somewhere you tab into.
          onOpenAutoFocus={(event) => event.preventDefault()}
          onInteractOutside={() => setOpen(false)}
          className="z-50 w-[var(--radix-popover-trigger-width)] min-w-[14rem] overflow-hidden rounded-md border border-hairline bg-page shadow-lg"
        >
          <div ref={listRef} role="listbox" className="max-h-72 overflow-y-auto p-1">
            {suggestions.length === 0 && (
              <p className="px-3 py-6 text-center text-sm text-ink-muted">
                {value.trim().length === 0 ? hintMessage : loading ? "Searching…" : emptyMessage}
              </p>
            )}

            {suggestions.map((suggestion, index) => (
              <button
                key={suggestion.id}
                type="button"
                role="option"
                aria-selected={index === activeIndex}
                data-active={index === activeIndex}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => commit(suggestion)}
                className={cn(
                  "flex w-full cursor-pointer flex-col items-start gap-0.5 rounded-md px-3 py-2 text-left",
                  index === activeIndex ? "bg-surface" : "",
                )}
              >
                <span className="w-full truncate text-sm text-ink-display">{suggestion.label}</span>
                {suggestion.hint && (
                  <span className="w-full truncate text-xs text-ink-muted">{suggestion.hint}</span>
                )}
              </button>
            ))}
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  )
}
